import { zodResolver } from "@hookform/resolvers/zod";
import SaveIcon from "@mui/icons-material/Save";
import { Box, CircularProgress, Divider, Fab, Grid, Typography } from "@mui/material";
import type { FieldPath } from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import AlbInput from "@/components/common/AlbInput";
import type { Document } from "@/components/common/upload";
import { Upload, UploadFormItem } from "@/components/common/upload";
import DRIVER_DOCS from "@/components/Delivery/utils/driver-docs-constants";
import { useUpdateDriverMutation } from "@/libs/generated/hooks";
import type { GetDriverByIdQuery } from "@/libs/generated/operations";
import { queryClient } from "@/libs/queryClient";
import type { DriverFormSchemaType } from "@/schemas/driver";
import { DriverFormSchema, GetDriverFormDefaultValues } from "@/schemas/driver";

type DriverType = GetDriverByIdQuery["getDriverById"];

type DriverFormSimpleProps = {
  data: DriverType;
  children?: React.ReactNode;
};

const booleanRadioOptions = [
  { name: "Oui", value: true },
  { name: "Non", value: false },
];

const businessStatusOptions = [
  { name: "Entreprise", value: "COMPANY" },
  { name: "Chauffeur", value: "DRIVER" },
];

const dirtyFieldStyles = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#ff5c8d !important",
      borderWidth: "2px",
    },
    "&:hover fieldset": {
      borderColor: "#ff5c8d !important",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#ff5c8d !important",
    },
  },
};

function FloatingSaveButton({ onClick, loading }: { onClick: () => void; loading: boolean }) {
  return (
    <Box
      sx={{
        position: "fixed",
        right: "3vw",
        bottom: "3vh",
        zIndex: 1000,
      }}
    >
      <Fab variant="extended" color="primary" onClick={onClick} disabled={loading}>
        {loading ? <CircularProgress size={20} /> : <SaveIcon sx={{ mr: 1 }} />}
        Sauvegarder
      </Fab>
    </Box>
  );
}

export default function DriverFormSimple({ data: driverData, children }: DriverFormSimpleProps) {
  const updateDriverMutation = useUpdateDriverMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["getDriverById"] });
    },
  });
  const loading = updateDriverMutation.isPending;

  const methods = useForm<DriverFormSchemaType>({
    resolver: zodResolver(DriverFormSchema),
    defaultValues: GetDriverFormDefaultValues(driverData),
  });

  const {
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { dirtyFields },
  } = methods;

  const hasChanges = Object.keys(dirtyFields).length > 0;

  const getDirtyValues = (data: DriverFormSchemaType): Partial<DriverFormSchemaType> => {
    const dirtyValues: Partial<DriverFormSchemaType> = { id: data.id };

    const addIfDirty = <K extends keyof DriverFormSchemaType>(
      fieldName: K,
      value: DriverFormSchemaType[K]
    ) => {
      if (dirtyFields[fieldName] !== undefined) {
        dirtyValues[fieldName] = value;
      }
    };

    addIfDirty("firstName", data.firstName);
    addIfDirty("lastName", data.lastName);
    addIfDirty("email", data.email);
    addIfDirty("phoneNumber", data.phoneNumber);
    addIfDirty("birthDate", data.birthDate);
    addIfDirty("drivingLicenseDate", data.drivingLicenseDate);
    addIfDirty("drivingPermitDueDate", data.drivingPermitDueDate);
    addIfDirty("drivingPermitNumber", data.drivingPermitNumber);
    addIfDirty("business_status", data.business_status);
    addIfDirty("iban", data.iban);
    addIfDirty("company", data.company);
    addIfDirty("siret", data.siret);
    addIfDirty("truck_ownership_status", data.truck_ownership_status);
    addIfDirty("wgarage", data.wgarage);
    addIfDirty("insurance_expiration_date", data.insurance_expiration_date);
    addIfDirty("vigilance_issue_date", data.vigilance_issue_date);
    addIfDirty("kbis_issue_date", data.kbis_issue_date);

    if (data.address && dirtyFields.address) {
      const dirtyAddress: typeof data.address = { id: data.address.id };
      const addrDirty = dirtyFields.address as Record<string, boolean>;

      if (addrDirty.address) dirtyAddress.address = data.address.address;
      if (addrDirty.city) dirtyAddress.city = data.address.city;
      if (addrDirty.postalCode) dirtyAddress.postalCode = data.address.postalCode;
      if (addrDirty.addressPrecision) dirtyAddress.addressPrecision = data.address.addressPrecision;

      dirtyValues.address = dirtyAddress;
    }

    if (dirtyFields.docs && data.docs) {
      dirtyValues.docs = data.docs;
    }

    return dirtyValues;
  };

  const onSubmit = async (formData: DriverFormSchemaType) => {
    if (!driverData?.id) return;

    const dirtyData = getDirtyValues(formData);

    try {
      await updateDriverMutation.mutateAsync({
        input: dirtyData,
      });

      reset(formData);
      toast.success("Les informations ont été enregistrées");
    } catch (_error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const isFieldDirty = (fieldName: string): boolean => {
    const fields = fieldName.split(".");
    if (fields.length === 1) {
      return Boolean(dirtyFields[fieldName as keyof DriverFormSchemaType]);
    }
    const parent = dirtyFields[fields[0] as keyof DriverFormSchemaType];
    if (parent !== null && typeof parent === "object") {
      return Boolean(parent[fields[1] as keyof typeof parent]);
    }
    return false;
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
      >
        <Box
          sx={{
            backgroundColor: "#E7E6F7",
            borderRadius: 1,
            padding: "20px",
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ color: "#00244f" }}>
            Informations chauffeur
          </Typography>
          <Divider sx={{ mb: 3, borderColor: "#00244f", opacity: 0.2 }} />

          <Grid container spacing={2}>
            {/* Row 1: Personal info */}
            <Grid size={{ xs: 12, md: 6 }} sx={isFieldDirty("lastName") ? dirtyFieldStyles : {}}>
              <AlbInput name="lastName" label="Nom" type="TextField" variant="outlined" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={isFieldDirty("firstName") ? dirtyFieldStyles : {}}>
              <AlbInput name="firstName" label="Prénom" type="TextField" variant="outlined" />
            </Grid>

            {/* Row 2: Contact */}
            <Grid size={{ xs: 12, md: 6 }} sx={isFieldDirty("email") ? dirtyFieldStyles : {}}>
              <AlbInput
                name="email"
                label="Email"
                type="TextField"
                inputType="email"
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={isFieldDirty("phoneNumber") ? dirtyFieldStyles : {}}>
              <AlbInput name="phoneNumber" label="Téléphone" type="PhoneField" variant="outlined" />
            </Grid>

            {/* Row 3: Birth date */}
            <Grid size={{ xs: 12 }} sx={isFieldDirty("birthDate") ? dirtyFieldStyles : {}}>
              <AlbInput
                name="birthDate"
                label="Date de naissance"
                type="DateField"
                variant="outlined"
              />
            </Grid>

            {/* Divider */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Row 4: Address */}
            <Grid size={{ xs: 12 }} sx={isFieldDirty("address.address") ? dirtyFieldStyles : {}}>
              <AlbInput
                name="address.address"
                label="Adresse"
                type="TextField"
                variant="outlined"
              />
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={isFieldDirty("address.postalCode") ? dirtyFieldStyles : {}}
            >
              <AlbInput
                name="address.postalCode"
                label="Code postal"
                type="TextField"
                variant="outlined"
              />
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={isFieldDirty("address.city") ? dirtyFieldStyles : {}}
            >
              <AlbInput name="address.city" label="Ville" type="TextField" variant="outlined" />
            </Grid>
            <Grid
              size={{ xs: 12 }}
              sx={isFieldDirty("address.addressPrecision") ? dirtyFieldStyles : {}}
            >
              <AlbInput
                name="address.addressPrecision"
                label="Complément d'adresse"
                type="TextField"
                variant="outlined"
              />
            </Grid>

            {/* Divider */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Row 5: Company */}
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={isFieldDirty("business_status") ? dirtyFieldStyles : {}}
            >
              <AlbInput
                name="business_status"
                label="Statut"
                type="SelectField"
                variant="outlined"
                options={businessStatusOptions}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={isFieldDirty("company") ? dirtyFieldStyles : {}}>
              <AlbInput name="company" label="Société" type="TextField" variant="outlined" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={isFieldDirty("siret") ? dirtyFieldStyles : {}}>
              <AlbInput name="siret" label="SIRET" type="TextField" variant="outlined" />
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={isFieldDirty("kbis_issue_date") ? dirtyFieldStyles : {}}
            >
              <AlbInput
                name="kbis_issue_date"
                label="Date de délivrance KBIS"
                type="DateField"
                variant="outlined"
              />
            </Grid>

            {/* Divider */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Row 6: License */}
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={isFieldDirty("drivingPermitNumber") ? dirtyFieldStyles : {}}
            >
              <AlbInput
                name="drivingPermitNumber"
                label="N° Permis"
                type="TextField"
                variant="outlined"
              />
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={isFieldDirty("drivingLicenseDate") ? dirtyFieldStyles : {}}
            >
              <AlbInput
                name="drivingLicenseDate"
                label="Date d'obtention du permis"
                type="DateField"
                variant="outlined"
              />
            </Grid>
            <Grid
              size={{ xs: 12 }}
              sx={isFieldDirty("drivingPermitDueDate") ? dirtyFieldStyles : {}}
            >
              <AlbInput
                name="drivingPermitDueDate"
                label="Date d'échéance du permis"
                type="DateField"
                variant="outlined"
              />
            </Grid>

            {/* Row 7: Boolean fields */}
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={isFieldDirty("truck_ownership_status") ? dirtyFieldStyles : {}}
            >
              <AlbInput
                name="truck_ownership_status"
                label="Camion propriété"
                type="RadioField"
                variant="outlined"
                options={booleanRadioOptions}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={isFieldDirty("wgarage") ? dirtyFieldStyles : {}}>
              <AlbInput
                name="wgarage"
                label="WGarage"
                type="RadioField"
                variant="outlined"
                options={booleanRadioOptions}
              />
            </Grid>

            {/* Row 8: Dates */}
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={isFieldDirty("insurance_expiration_date") ? dirtyFieldStyles : {}}
            >
              <AlbInput
                name="insurance_expiration_date"
                label="Date d'expiration assurance"
                type="DateField"
                variant="outlined"
              />
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={isFieldDirty("vigilance_issue_date") ? dirtyFieldStyles : {}}
            >
              <AlbInput
                name="vigilance_issue_date"
                label="Date certificat de vigilance"
                type="DateField"
                variant="outlined"
              />
            </Grid>

            {/* Divider */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Row 9: Payment */}
            <Grid size={{ xs: 12 }} sx={isFieldDirty("iban") ? dirtyFieldStyles : {}}>
              <AlbInput name="iban" label="IBAN" type="TextField" variant="outlined" />
            </Grid>

            {/* Divider */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Documents */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom>
                Documents
              </Typography>
              <Upload.Root>
                {DRIVER_DOCS.map((doc: Document & { id: string }, index: number) => (
                  <UploadFormItem<DriverFormSchemaType>
                    key={doc.id}
                    name={`docs.${index}` as FieldPath<DriverFormSchemaType>}
                    meta={{
                      folder: `driver-application/${driverData?.email}/${doc.id}`,
                      subFolder: driverData?.email ?? "",
                    }}
                    handleDelete={(deletedKey) => {
                      const docs = getValues("docs");
                      const targetIndex =
                        docs?.findIndex((item) => item?.key === deletedKey) ?? index;
                      const resolvedIndex = targetIndex >= 0 ? targetIndex : index;
                      const docTemplate = DRIVER_DOCS[resolvedIndex] ?? doc;

                      setValue(
                        `docs.${resolvedIndex}` as FieldPath<DriverFormSchemaType>,
                        {
                          ...docTemplate,
                          key: "",
                          url: "",
                          metadata: { id: docTemplate.id },
                        },
                        { shouldDirty: true, shouldTouch: true, shouldValidate: true }
                      );
                    }}
                  />
                ))}
              </Upload.Root>
            </Grid>

            {/* Actions from parent */}
            {children !== null && children !== undefined && (
              <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                {children}
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Floating Save Button */}
        {hasChanges && (
          <FloatingSaveButton
            onClick={() => {
              void handleSubmit(onSubmit)();
            }}
            loading={loading}
          />
        )}
      </form>
    </FormProvider>
  );
}
